import React, { useEffect, useState } from 'react';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import { usePurchasePage } from './PurchasePageContext';
import { getGameTypeConfig } from '../../../config/gameTypes';
import { useCountdown } from '../../../hooks/useCountdown';
import DrawDateBadge from '../../../components/DrawDateBadge';
import { getDrawDateValue } from '../../../utils/dateFormat';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useContent } from '../../../contexts/ContentContext';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import DrawLifecycleMessage from '../../../components/DrawLifecycleMessage';
import { getDrawLifecycleState } from '../../../utils/drawLifecycle';
import api, { apiRoutes } from '../../../api';
import { getConfig } from '../../../config/environment';
import { getHeroPrize, getLocalizedPrizeField, getPrizeImageUrl } from '../../../utils/prizeHelpers';
import { pickLocalized } from '../../../utils/drawsApi';

export default function PurchaseHeroSection({ section, config }) {
  const { gameType, mainDraw } = usePurchasePage();
  const { c } = useContent();
  const { t: tr } = useTranslations();
  const { language } = useLanguage();
  const gameConfig = getGameTypeConfig(gameType);
  const { tlfId } = getConfig();

  const drawDate = getDrawDateValue(mainDraw);
  const countdown = useCountdown(drawDate);
  const lifecycleState = getDrawLifecycleState({
    draw: mainDraw,
    countdownComplete: countdown.isComplete,
    gameType,
  });

  // For prize draws, feature the prize photo/name/description instead of a
  // jackpot dollar amount.
  const isPrizeDraw = gameType === 'prizeDraw';
  const [prizes, setPrizes] = useState(() => mainDraw?.prizes || []);
  useEffect(() => {
    let cancelled = false;
    if (!isPrizeDraw || !mainDraw?.id) {
      setPrizes(mainDraw?.prizes || []);
      return undefined;
    }
    (async () => {
      try {
        const res = await api.get(apiRoutes.PrizeDrawPrizes, {
          params: { orgId: tlfId },
        });
        const prizesData = res.data?.payload?.prizes || res.data?.prizes || [];
        // Normalise: API returns camelCase imageUrl; downstream code uses image_url
        const normalised = prizesData.map(p => ({ ...p, image_url: p.image_url || p.imageUrl || null }));
        if (!cancelled) setPrizes(normalised);
      } catch (err) {
        console.warn('[PurchaseHero] Could not fetch prizes:', err);
        if (!cancelled) setPrizes(mainDraw?.prizes || []);
      }
    })();
    return () => { cancelled = true; };
  }, [isPrizeDraw, mainDraw?.id, mainDraw?.prizes, tlfId]);

  const normMap = { en: 'English', fr: 'French', es: 'Spanish' };
  const fullLanguage = normMap[language] || 'English';

  const drawPrizeName = pickLocalized(mainDraw?.prizeNames || mainDraw?.prize_names, fullLanguage, mainDraw?.prize_name || mainDraw?.prizeName || '');
  const drawPrizeDesc = pickLocalized(mainDraw?.prizeDescriptions || mainDraw?.prize_descriptions, fullLanguage, mainDraw?.prize_description || mainDraw?.prizeDescription || '');

  const heroPrize = isPrizeDraw ? getHeroPrize(prizes) : null;
  const rawPrizeName = heroPrize ? getLocalizedPrizeField(heroPrize.name, fullLanguage) : '';
  const rawPrizeDesc = heroPrize ? getLocalizedPrizeField(heroPrize.description, fullLanguage) : '';

  const isGeneric = (str) => !str || ['grand prize', 'grand', '1st prize', 'first prize'].includes(String(str).trim().toLowerCase());

  const prizeName = (!isGeneric(rawPrizeName) ? rawPrizeName : '') || (!isGeneric(rawPrizeDesc) ? rawPrizeDesc : '') || drawPrizeName || drawPrizeDesc || rawPrizeName || '';
  const prizeDescription = rawPrizeDesc && rawPrizeDesc !== prizeName ? rawPrizeDesc : (drawPrizeDesc && drawPrizeDesc !== prizeName ? drawPrizeDesc : '');

  const is5050 = gameType === 'classic5050' || gameType === 'modified5050' || gameType === 'progressive5050' || String(gameType).includes('5050');
  const actualJackpotKey = is5050 ? 'classic5050.winnerTakesHome' : 'chaseTheAce.actualJackpotAmount';
  const actualJackpotFallback = is5050 ? 'Winner Takes Home' : 'Actual Jackpot Amount';

  // Format jackpot/prize pool
  const jackpot = mainDraw?.jackpot || mainDraw?.prizePool || 0;
  const formattedJackpot = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(jackpot);

  // Weekly pot for Chase the Ace
  const weeklyPrize = gameType === 'chaseTheAce'
    ? Math.floor(mainDraw?.weekly_prize ?? mainDraw?.secondary_prize ?? 0)
    : 0;
  const formattedWeeklyPrize = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(weeklyPrize);

  // For Chase the Ace, prefer the organization's actual game/card name from
  // the draw (e.g. "Le Roi de cœur") over the generic "Chase the Ace" label.
  const heroTitleFallback =
    (gameType === 'chaseTheAce' && mainDraw?.name) ||
    gameConfig?.name ||
    'Purchase Tickets';

  return (
    <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white rounded-2xl p-2 sm:p-5 md:p-8 lg:p-5 mb-1.5 sm:mb-6 md:mb-8 lg:mb-0 shadow-2xl">
      <div className="max-w-4xl lg:max-w-none mx-auto text-center lg:grid lg:grid-cols-[1.1fr_1fr_1fr] lg:items-center lg:gap-6">
        <div className="lg:text-left">
          {/* Game title */}
          <div className="flex items-center justify-center lg:justify-start gap-1.5 sm:gap-3 mb-0.5 sm:mb-3 lg:mb-2">
            <Trophy className="w-4 h-4 sm:w-8 sm:h-8 md:w-10 md:h-10 lg:w-8 lg:h-8 text-yellow-300 animate-pulse" />
            <h1 className="text-lg sm:text-3xl md:text-5xl lg:text-3xl font-bold">
              <InlineTranslationEdit
                translationKey={`purchase.hero.title.${gameType}`}
                value={c(`purchase.hero.title.${gameType}`, tr(`purchase.hero.title.${gameType}`) || heroTitleFallback)}
                defaultText={heroTitleFallback}
              />
            </h1>
          </div>

          {/* Subtitle */}
          <p className="text-[11px] sm:text-base md:text-lg lg:text-sm text-blue-100 mb-1.5 sm:mb-4 md:mb-6 lg:mb-0">
            <InlineTranslationEdit
              translationKey={`purchase.hero.subtitle.${gameType}`}
                value={c(`purchase.hero.subtitle.${gameType}`, tr(`purchase.hero.subtitle.${gameType}`) || tr('purchase.hero.subtitle.chaseTheAce') || 'Select your tickets and join the game!')}
              defaultText="Select your tickets and join the game!"
            />
          </p>
        </div>

        {/* Prize draw: feature the prize photo/name/description */}
        {isPrizeDraw && heroPrize ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 sm:p-6 lg:p-3 mb-2 sm:mb-6 lg:mb-0">
            {getPrizeImageUrl(heroPrize) && (
            <div className="mx-auto max-w-[7rem] sm:max-w-xs lg:max-w-[7rem] mb-1.5 sm:mb-4 lg:mb-2">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden ring-2 ring-white/40 shadow-2xl">
                <img
                  src={getPrizeImageUrl(heroPrize)}
                  alt={prizeName || 'Prize'}
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
            )}
            {prizeName && (
              <div className="text-base sm:text-2xl md:text-3xl font-black text-yellow-300 mt-1 sm:mt-4">
                {prizeName}
              </div>
            )}
            {prizeDescription && (
              <p className="text-[11px] sm:text-sm md:text-base text-blue-100 mt-0.5 sm:mt-2 max-w-xl mx-auto">
                {prizeDescription}
              </p>
            )}
          </div>
        ) : (
          /* Jackpot display — never shown for prize draws */
          !isPrizeDraw && jackpot > 0 && (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-2 sm:p-6 lg:p-4 mb-2 sm:mb-6 lg:mb-0 max-w-md mx-auto lg:w-full">
              {weeklyPrize > 0 ? (
                <div className="flex flex-col items-center justify-center w-full">
                  <div className="flex items-center justify-around gap-2 sm:gap-4 w-full">
                    <div className="flex flex-col items-center">
                      <div className="text-[9px] sm:text-xs text-blue-200 uppercase tracking-wider mb-0.5 sm:mb-1 flex items-center justify-center gap-1">
                        <span>
                          <InlineTranslationEdit
                            translationKey="purchase.hero.currentJackpot"
                            value={c('purchase.hero.currentJackpot', tr('purchase.hero.currentJackpot') || 'Current Jackpot')}
                            defaultText="Current Jackpot"
                          />
                        </span>
                        {mainDraw?.show_jackpot_guarantee_text && (
                          <span className="text-[7px] px-1 bg-yellow-400/20 text-yellow-300 border border-yellow-400/35 rounded font-semibold uppercase leading-none tracking-normal">
                            Guaranteed
                          </span>
                        )}
                      </div>
                      <div className="text-lg sm:text-3xl md:text-5xl font-black text-yellow-300 leading-none">
                        {formattedJackpot}
                      </div>
                    </div>

                    <div className="w-px self-stretch bg-white/20 my-0.5" />

                    <div className="flex flex-col items-center">
                      <div className="text-[9px] sm:text-xs text-blue-200 uppercase tracking-wider mb-0.5 sm:mb-1">
                        <InlineTranslationEdit
                          translationKey="purchase.hero.weeklyPot"
                          value={c('purchase.hero.weeklyPot', tr('purchase.hero.weeklyPot') || 'Weekly Pot')}
                          defaultText="Weekly Pot"
                        />
                      </div>
                      <div className="text-base sm:text-2xl md:text-3xl font-black text-white leading-none">
                        {formattedWeeklyPrize}
                      </div>
                    </div>
                  </div>
                  
                  {mainDraw?.show_jackpot_guarantee_amount && mainDraw?.actual_jackpot_amount && (
                    <div className="mt-4 pt-3 border-t border-white/10 w-full text-center">
                      <p className="text-xs text-white/80">
                        <InlineTranslationEdit
                          translationKey={actualJackpotKey}
                          value={c(actualJackpotKey)}
                          fallback={actualJackpotFallback}
                        />: ${mainDraw.actual_jackpot_amount.toLocaleString()}
                      </p>
                      <p className="text-[10px] text-white/50 italic mt-0.5 max-w-sm mx-auto leading-normal">
                        <InlineTranslationEdit
                          translationKey="chaseTheAce.actualJackpotExplanation"
                          value={c('chaseTheAce.actualJackpotExplanation')}
                          fallback="The guaranteed jackpot is paid regardless of ticket sales. The actual jackpot shows the contribution amount generated from ticket sales so far."
                        />
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="text-[9px] sm:text-xs text-blue-200 uppercase tracking-wider mb-0.5 sm:mb-1 flex items-center justify-center gap-1.5">
                    <span>
                      <InlineTranslationEdit
                        translationKey="purchase.hero.currentJackpot"
                        value={c('purchase.hero.currentJackpot', tr('purchase.hero.currentJackpot') || 'Current Jackpot')}
                        defaultText="Current Jackpot"
                      />
                    </span>
                    {mainDraw?.show_jackpot_guarantee_text && (
                      <span className="text-[8px] px-1.5 py-0.5 bg-yellow-400/20 text-yellow-300 border border-yellow-400/30 rounded font-semibold uppercase leading-none tracking-normal">
                        Guaranteed
                      </span>
                    )}
                  </div>
                  <div className="text-xl sm:text-4xl md:text-6xl font-black text-yellow-300 leading-none">
                    {formattedJackpot}
                  </div>
                  
                  {mainDraw?.show_jackpot_guarantee_amount && mainDraw?.actual_jackpot_amount && (
                    <div className="mt-3 pt-3 border-t border-white/10 w-full text-center">
                      <p className="text-xs text-white/80">
                        <InlineTranslationEdit
                          translationKey={actualJackpotKey}
                          value={c(actualJackpotKey)}
                          fallback={actualJackpotFallback}
                        />: ${mainDraw.actual_jackpot_amount.toLocaleString()}
                      </p>
                      <p className="text-[10px] text-white/50 italic mt-0.5 max-w-sm mx-auto leading-normal">
                        <InlineTranslationEdit
                          translationKey="chaseTheAce.actualJackpotExplanation"
                          value={c('chaseTheAce.actualJackpotExplanation')}
                          fallback="The guaranteed jackpot is paid regardless of ticket sales. The actual jackpot shows the contribution amount generated from ticket sales so far."
                        />
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )
        )}

        <div className="lg:flex lg:flex-col lg:items-center lg:justify-center">
          <DrawDateBadge draw={mainDraw} className="mb-1.5 sm:mb-4 md:mb-6 lg:mb-3" compact />

          {/* Draw countdown */}
          {drawDate && lifecycleState ? (
            <DrawLifecycleMessage state={lifecycleState} compact className="mx-auto max-w-xl" />
          ) : drawDate && countdown ? (
            <div className="flex items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm md:text-base lg:text-sm">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>
                  <InlineTranslationEdit
                    translationKey="purchase.hero.drawIn"
                    value={c('purchase.hero.drawIn', tr('purchase.hero.drawIn') || 'Draw in')}
                    defaultText="Draw in"
                  /> <span className="font-bold">{countdown.timeLeft.days}{tr('purchase.hero.daysShort', 'd')} {countdown.timeLeft.hours}{tr('purchase.hero.hoursShort', 'h')} {countdown.timeLeft.minutes}{tr('purchase.hero.minutesShort', 'm')}</span>
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
